// src/views/Admin/Locales/LocalPage/components/SalesStatsCard/SalesStatsCard.tsx

import React from 'react';
import { 
  HiOutlineBanknotes, 
  HiOutlineReceiptPercent,
  HiOutlineDocumentText,
  HiOutlineClock
} from 'react-icons/hi2';
import { FiTrendingUp, FiGift } from 'react-icons/fi';
import './SalesStatsCard.scss';

interface SalesStatsCardProps {
  title: string;
  subtitle?: string;
  type: 'realtime' | 'historical';
  stats: {
    grossSales?: number;
    netSales?: number;
    tickets?: number;
    averageTicket?: number;
    tips?: number;
    pendingPayments?: number;
    totalSessions?: number;
  };
}

const SalesStatsCard: React.FC<SalesStatsCardProps> = ({
  title,
  subtitle,
  type,
  stats
}) => {
  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return '0';
    return new Intl.NumberFormat('es-CR').format(value);
  };

  const statsConfig = [
    {
      key: 'grossSales',
      label: 'Ventas Brutas',
      icon: HiOutlineBanknotes,
      color: 'green',
      format: formatCurrency,
      show: stats.grossSales !== undefined
    },
    {
      key: 'netSales',
      label: 'Ventas Netas',
      icon: HiOutlineBanknotes,
      color: 'blue',
      format: formatCurrency,
      show: stats.netSales !== undefined
    },
    {
      key: 'tickets',
      label: 'Boletas',
      icon: HiOutlineReceiptPercent,
      color: 'orange',
      format: formatNumber,
      show: stats.tickets !== undefined
    },
    {
      key: 'averageTicket',
      label: 'Ticket Promedio',
      icon: FiTrendingUp,
      color: 'purple',
      format: formatCurrency,
      show: stats.averageTicket !== undefined
    },
    {
      key: 'tips',
      label: 'Propinas',
      icon: FiGift,
      color: 'teal',
      format: formatCurrency,
      show: stats.tips !== undefined
    },
    {
      key: 'pendingPayments',
      label: 'Pagos Pendientes',
      icon: HiOutlineClock,
      color: 'yellow',
      format: formatNumber,
      show: stats.pendingPayments !== undefined
    },
    {
      key: 'totalSessions',
      label: 'Total Sesiones',
      icon: HiOutlineDocumentText,
      color: 'indigo',
      format: formatNumber,
      show: stats.totalSessions !== undefined
    }
  ];

  const visibleStats = statsConfig.filter(s => s.show);

  return (
    <div className={`sales-stats-card ${type}`}>
      <div className="sales-stats-header">
        <div className="header-content">
          <h3>{title}</h3>
          {subtitle && <span className="subtitle">{subtitle}</span>}
        </div>
        {type === 'realtime' && (
          <div className="live-badge">
            <span className="live-dot" />
            En vivo
          </div>
        )}
      </div>
      
      <div className="sales-stats-grid">
        {visibleStats.map(({ key, label, icon: Icon, color, format }) => (
          <div key={key} className="stat-item">
            <div className={`stat-icon ${color}`}>
              <Icon size={22} />
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {format(stats[key as keyof typeof stats] as number)}
              </span>
              <span className="stat-label">{label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SalesStatsCard;
